import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Form, Input, Button, Image } from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../../views/components/reuseComp/ButtonWrapper";
import { Copyright } from "lucide-react";
import UploadImage from "../../components/UploadImage";

interface FooterData {
  about?: string;
  linkedin?: string;
  facebook?: string;
  twitter?: string;
  logo?: {
    light?: string;
    dark?: string;
  };
  copyright?: string;
}

const Footer: React.FC<{
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  footerData?: FooterData;
  loading?: boolean;
}> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  loading,
  footerData,
}) => {
  useEffect(() => {
    if (footerData) {
      form.setFieldsValue({
        footer: {
          logo: {
            light: footerData?.logo?.light || "",
            dark: footerData?.logo?.dark || "",
          },
          about: footerData.about || "",
          linkedin: footerData.linkedin || "",
          facebook: footerData.facebook || "",
          twitter: footerData.twitter || "",
          copyright: footerData.copyright || "",
        },
      });
    } else {
      form.resetFields();
    }
  }, [footerData]);

  return (
    <div>
      {!isEditing ? (
        <>
          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Logo</th>
                  <th className="py-3 px-4 text-left">About</th>
                  <th className="py-3 px-4 text-left">Copyright</th>
                  <th className="py-3 px-4 text-left">Links</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {footerData ? (
                  <tr className="bg-[#F9F9F9] border-b">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-4\">
                        <div className="text-center">
                          {footerData?.logo?.light ? (
                            <Image
                              src={
                                footerData?.logo?.light || "/placeholder.jpg"
                              }
                              height={56}
                              width={56}
                              alt="logo"
                              className="object-contain"
                            />
                          ) : (
                            <span className="text-xs text-gray-400">
                              Light —
                            </span>
                          )}
                          <p className="text-xs mt-1">Light</p>
                        </div>

                        <div className="text-center">
                          {footerData?.logo?.dark ? (
                            <Image
                              src={footerData?.logo?.dark || "/placeholder.jpg"}
                              height={56}
                              width={56}
                              alt="logo"
                              className="object-contain"
                            />
                          ) : (
                            <span className="text-xs text-gray-400">
                              Dark —
                            </span>
                          )}
                          <p className="text-xs mt-1">Dark</p>
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4">{footerData.about}</td>
                    <td className="py-3 px-4">{footerData.copyright}</td>

                    <td className="py-3 px-4 space-y-1">
                      <div>
                        <strong>LinkedIn: </strong>
                        {footerData.linkedin ? (
                          <a
                            href={footerData.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {footerData.linkedin}
                          </a>
                        ) : (
                          "—"
                        )}
                      </div>

                      <div>
                        <strong>Facebook: </strong>
                        {footerData.facebook ? (
                          <a
                            href={footerData.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {footerData.facebook}
                          </a>
                        ) : (
                          "—"
                        )}
                      </div>

                      <div>
                        <strong>Twitter: </strong>
                        {footerData.twitter ? (
                          <a
                            href={footerData.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {footerData.twitter}
                          </a>
                        ) : (
                          "—"
                        )}
                      </div>
                    </td>

                    <td className="py-3 px-4 text-right">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-primary text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Footer Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden mt-3 space-y-4">
            <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
              <div>
                <span className="font-semibold text-primary block mb-1">
                  Logos:
                </span>
                <div className="flex gap-4">
                  {footerData?.logo?.light && (
                    <Image
                      src={footerData?.logo?.light || "/placeholder.jpg"}
                      height={64}
                      width={64}
                      alt="logo"
                      className="object-contain"
                    />
                  )}
                  {footerData?.logo?.dark && (
                    <Image
                      src={footerData?.logo?.dark || "/placeholder.jpg"}
                      height={64}
                      width={64}
                      alt="logo"
                      className="object-contain"
                    />
                  )}
                </div>
              </div>

              <div>
                <span className="font-semibold text-primary block mb-1">
                  About:
                </span>
                <p>{footerData?.about || "—"}</p>
              </div>

              <div>
                <span className="font-semibold text-primary block mb-1">
                  Copyright:
                </span>
                <p>{footerData?.copyright || "—"}</p>
              </div>

              <div>
                <span className="font-semibold text-primary block mb-1">
                  Links:
                </span>

                <p>
                  <strong>LinkedIn:</strong>{" "}
                  {footerData?.linkedin ? (
                    <a
                      href={footerData.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {footerData.linkedin}
                    </a>
                  ) : (
                    "—"
                  )}
                </p>

                <p>
                  <strong>Facebook:</strong>{" "}
                  {footerData?.facebook ? (
                    <a
                      href={footerData.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {footerData.facebook}
                    </a>
                  ) : (
                    "—"
                  )}
                </p>

                <p>
                  <strong>Twitter:</strong>{" "}
                  {footerData?.twitter ? (
                    <a
                      href={footerData.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {footerData.twitter}
                    </a>
                  ) : (
                    "—"
                  )}
                </p>
              </div>

              <div>
                <span className="font-semibold text-primary block mb-1">
                  Action:
                </span>
                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-primary text-[20px]"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-xl shadow-sm">
            
            <Form layout="vertical" form={form}>
              <div className="flex justify-between">
              <Form.Item
                label="Logo (Light)"
                name={["footer", "logo", "light"]}
                valuePropName="value"
                rules={[{ required: true, message: "Upload light logo" }]}
              >
                <UploadImage count={1} listType="picture-card" />
              </Form.Item>

              <Form.Item
                label="Logo (Dark)"
                name={["footer", "logo", "dark"]}
                valuePropName="value"
                rules={[{ required: true, message: "Upload dark logo" }]}
              >
                <UploadImage count={1} listType="picture-card" />
              </Form.Item>
              </div>
              <Form.Item
                label="About"
                name={["footer", "about"]}
                rules={[{ required: true, message: "Enter about text" }]}
              >
                <TextArea rows={3} placeholder="About Company" />
              </Form.Item>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Form.Item label="LinkedIn" name={["footer", "linkedin"]}>
                  <Input placeholder="LinkedIn URL" type="url" />
                </Form.Item>

                <Form.Item label="Facebook" name={["footer", "facebook"]}>
                  <Input placeholder="Facebook URL" type="url" />
                </Form.Item>

                <Form.Item label="Twitter" name={["footer", "twitter"]}>
                  <Input placeholder="Twitter URL" type="url" />
                </Form.Item>
              </div>

              <Form.Item
                label="Copyright"
                name={["footer", "copyright"]}
                rules={[{ required: true, message: "Enter copyright" }]}
              >
                <TextArea rows={1} placeholder="copyright..." />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3 mt-7">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default Footer;
